"use client";

import { useSyncExternalStore } from "react";
import { Box, Typography, Grid } from "@mui/material";
import Image from "next/image";
import { theme } from "@/lib/theme";
import { MoneyTransferCard } from "./MoneyTransferCard";

export const InteractiveHero = () => {
  const isMounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  );

  const scrollToContent = () => {
    const nextSection = document.getElementById("chapter-00");
    if (nextSection) {
      const heroHeight = window.innerHeight;
      window.scrollTo({ top: heroHeight, behavior: "smooth" });
    }
  };

  if (!isMounted) {
    return (
      <Box
        sx={{
          background: `linear-gradient(to bottom, ${theme.colors.lightLime}, ${theme.colors.darkLimeGreen})`,
          minHeight: "100vh",
          width: "100%",
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        overflowY: "visible",
        marginLeft: "50%",
        left: "-50vw",
        marginTop: { xs: "-64px", sm: "-80px" },
        marginBottom: 0,
        boxSizing: "border-box",
        background: `linear-gradient(to bottom, ${theme.colors.lightLime} 0%, ${theme.colors.darkLimeGreen} 80%)`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: "url(/sections/00-introduction/images/dots.gif)",
          backgroundRepeat: "repeat",
          backgroundSize: "150px 122px",
          backgroundPosition: "0 0",
          opacity: 0.6,
          mixBlendMode: "multiply",
          pointerEvents: "none",
          zIndex: 0,
        },
        "& > *": {
          position: "relative",
          zIndex: 1,
        },
      }}
    >
      <Grid
        container
        justifyContent="center"
        sx={{
          pt: { xs: 3, sm: 4 },
          mb: { xs: 3, sm: 4 },
        }}
      >
        <Grid
          container
          alignItems="center"
          alignContent="center"
          justifyContent="center"
          spacing={1.5}
          sx={{
            width: "auto",
          }}
        >
          <Grid>
            <Box
              sx={{
                width: { xs: 28, sm: 36 },
                height: { xs: 28, sm: 36 },
                position: "relative",
              }}
            >
              <Image
                src="/images/capital-logo.svg"
                alt="Convocation Capital"
                fill
                style={{ objectFit: "contain" }}
              />
            </Box>
          </Grid>
          <Grid>
            <Typography
              sx={{
                fontFamily: theme.fonts.sans,
                fontSize: { xs: "18px", sm: "24px" },
                fontWeight: theme.fonts.weights.bold,
                color: theme.colors.purple,
                lineHeight: 1,
                margin: "0 !important",
              }}
            >
              Convocation Capital
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 3, md: 4 },
          px: { xs: 3, sm: 4, md: 6 },
          maxWidth: "1400px",
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            flex: { xs: 1, md: "0 1 45%" },
            maxWidth: { md: "45%" },
            display: "flex",
            flexDirection: "column",
            gap: 2,
            position: "relative",
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontFamily: theme.fonts.sans,
              fontSize: { xs: "40px", sm: "52px", md: "60px" },
              fontWeight: theme.fonts.weights.bold,
              lineHeight: 1.1,
              color: theme.colors.black,
            }}
          >
            Send Money Globally for Less?
          </Typography>
          <Typography
            sx={{
              fontFamily: theme.fonts.sans,
              fontSize: { xs: "20px", sm: "26px", md: "30px" },
              fontWeight: theme.fonts.weights.semibold,
              color: theme.colors.purple,
            }}
          >
            Terrible rates. Hidden fees.
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            width: { xs: "100%", md: "auto" },
            maxWidth: { xs: "100%", md: "450px" },
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              backgroundColor: theme.colors.white,
              color: theme.colors.black,
              padding: "12px 28px",
              borderRadius: "50px",
              fontSize: { xs: "15px", sm: "16px" },
              fontWeight: theme.fonts.weights.semibold,
              textTransform: "none",
              alignSelf: "flex-start",
              position: "absolute",
              bottom: "24px",
              right: "calc(100% + 20px)",
              whiteSpace: "nowrap",
              cursor: "default",
              userSelect: "none",
              "@keyframes float": {
                "0%, 100%": {
                  transform: "translateY(0)",
                },
                "10%": {
                  transform: "translateY(-8px)",
                },
                "15%": {
                  transform: "translateY(0)",
                },
                "20%, 90%": {
                  transform: "translateY(0)",
                },
              },
              animation: "float 3s ease-in-out infinite",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            Try sending money →
          </Box>
          <MoneyTransferCard />
        </Box>
      </Box>

      <Box
        onClick={scrollToContent}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0,
          pb: { xs: 6, sm: 8 },
          pt: { xs: 4, sm: 5 },
          cursor: "pointer",
          "&:hover": {
            opacity: 0.8,
          },
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: theme.fonts.weights.bold,
            color: theme.colors.purple,
            mb: "5px !important",
          }}
        >
          Dark Patterns in Finance and Design
        </Typography>
        <Box
          sx={{
            width: { xs: 28, sm: 32 },
            height: { xs: 36, sm: 40 },
            borderRadius: "16px",
            border: `2px solid ${theme.colors.purple}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: theme.colors.purple,
            fontSize: { xs: "18px", sm: "20px" },
            m: 0,
            p: 0,
          }}
        >
          ↓
        </Box>
      </Box>
    </Box>
  );
};
